import React from 'react';
import { DollarSign, Heart, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const Donate = () => {
  const donationAmounts = [10, 25, 50, 100, 250, 500];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-navy-900 to-navy-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">Support Our Campaign</h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Your contribution powers our grassroots, people-funded movement
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Matching Funds Explanation */}
            <Card className="mb-12 bg-red-50 border-red-200">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <Heart className="w-12 h-12 text-red-600 flex-shrink-0" />
                  <div>
                    <h2 className="text-2xl font-bold text-navy-900 mb-3">
                      Your Donation Goes Further
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      New York State's public campaign finance program can match eligible small-dollar donations 
                      from district residents. Your contribution not only supports our campaign directly but may 
                      also qualify for matching funds, amplifying your impact in the fight for better schools, 
                      safer communities, and an affordable New York.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Donation Buttons */}
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-navy-900 mb-8">Choose Your Contribution</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {donationAmounts.map((amount) => (
                  <Button
                    key={amount}
                    size="lg"
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold text-xl py-8"
                    onClick={() => toast.info('Donation link will be added soon')}
                  >
                    ${amount}
                  </Button>
                ))}
              </div>
              <Button
                size="lg"
                className="bg-navy-900 hover:bg-navy-800 text-white font-semibold px-12 py-6 text-xl"
                onClick={() => toast.info('Donation link will be added soon')}
              >
                Donate Other Amount
              </Button>
              <p className="text-sm text-gray-500 mt-6">
                Secure donation processing coming soon. Links will be updated with payment integration.
              </p>
            </div>

            {/* Why Donate */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-navy-900 mb-8 text-center">
                Why Your Support Matters
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardContent className="pt-8 pb-6 text-center">
                    <DollarSign className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-navy-900 mb-3">Grassroots Campaign</h3>
                    <p className="text-gray-600">
                      We're powered by individuals, not special interests. Every dollar supports our direct 
                      outreach to voters.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-8 pb-6 text-center">
                    <Users className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-navy-900 mb-3">Community First</h3>
                    <p className="text-gray-600">
                      Your support funds voter contact, volunteer training, and community events across 
                      the district.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-8 pb-6 text-center">
                    <Heart className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-navy-900 mb-3">Real Change</h3>
                    <p className="text-gray-600">
                      Together we can bring common-sense leadership to Albany and fight for New York families.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Legal Disclaimer */}
            <Card className="bg-gray-50">
              <CardContent className="p-6">
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong>Legal Notice:</strong> Contributions are not tax deductible. New York State law requires 
                  us to collect and report the name, mailing address, occupation, and employer of individuals whose 
                  contributions aggregate in excess of $99 in a calendar year. Contributions from corporations, labor 
                  unions, and foreign nationals are prohibited. Federal law requires us to use our best efforts to 
                  collect and report the name, mailing address, occupation, and employer of individuals whose 
                  contributions exceed $200 per election cycle.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Other Ways to Help */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-navy-900 mb-4">
            Can't Donate? There Are Other Ways to Help!
          </h2>
          <p className="text-gray-700 text-lg mb-8 max-w-2xl mx-auto">
            Your time is just as valuable as your money. Join our volunteer team and help us reach voters 
            across the district.
          </p>
          <Button 
            size="lg" 
            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8"
            onClick={() => window.location.href = '/volunteer'}
          >
            Volunteer Today
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Donate;
